import type { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  useFisSolr,
  useHasSapS4HanaIntegration,
} from "../sap-s4-hana/hooks.js";
import { createContingencyCheckboxColumn } from "./columns/contingencyCheckboxColumn.js";
import { createDecisionBehaviorColumn } from "./columns/decisionBehaviorColumn.js";
import { createDecisionOptionColumn } from "./columns/decisionOptionColumn.js";
import { createDescriptionColumn } from "./columns/descriptionColumn.js";
import { createEstimatedQuantityColumn } from "./columns/estimatedQuantityColumn.js";
import { createEstimatedTimeColumn } from "./columns/estimatedTimeColumn.js";
import { createManufacturerArticleNumberColumn } from "./columns/manufacturerArticleNumberColumn.js";
import { createMaterialBuyingPriceColumn } from "./columns/materialBuyingPriceColumn.js";
import { createMaterialFactorColumn } from "./columns/materialFactorColumn.js";
import { createMaterialMarginColumn } from "./columns/materialMarginColumn.js";
import { createMaterialPricePerUnitColumn } from "./columns/materialPricePerUnitColumn.js";
import { createPricePerUnitColumn } from "./columns/pricePerUnitColumn.js";
import { createPriceSubTotalColumn } from "./columns/priceSubTotalColumn.js";
import { createProductArticleNumberColumn } from "./columns/productArticleNumberColumn.js";
import { createProductBrandLineColumn } from "./columns/productBrandLineColumn.js";
import { createProductCategoryColumn } from "./columns/productCategoryColumn.js";
import { createProductDescriptionColumn } from "./columns/productDescriptionColumn.js";
import { createProductDescriptionVisibilityColumn } from "./columns/productDescriptionVisibilityColumn.js";
import { createProductGtinColumn } from "./columns/productGtinColumn.js";
import { createProductSapConfigColumn } from "./columns/productSapConfigColumn.js";
import { createProductSupplierColumn } from "./columns/productSupplierColumn.js";
import { createProductTitleColumn } from "./columns/productTitleColumn.js";
import { createProductTitleVisibilityColumn } from "./columns/productTitleVisibilityColumn.js";
import { createQuantityUnitColumn } from "./columns/quantityUnitColumn.js";
import { createSavingIndicatorColumn } from "./columns/savingIndicatorColumn.js";
import { createTimeTotalColumn } from "./columns/timeTotalColumn.js";
import { createWorkFactorColumn } from "./columns/workFactorColumn.js";
import { createWorkMarginColumn } from "./columns/workMarginColumn.js";
import { createWorkPricePerUnitColumn } from "./columns/workPricePerUnitColumn.js";
import { createWorkSellingRateColumn } from "./columns/workSellingRateColumn.js";
import { useContingencyCheckbox } from "./useContingencyCheckbox.js";
import { useDecisionBehaviorToggle } from "./useDecisionBehaviorToggle.js";
import { useDecisionOptionToggle } from "./useDecisionOptionToggle.js";
import { useModifyItemCalculation } from "./useModifyItemCalculation.js";
import { useModifyItemInfo } from "./useModifyItemInfo.js";
import { useModifyItemProduct } from "./useModifyItemProduct.js";
import { useSetItemProduct } from "./useSetItemProduct.js";
import type { DocDataGridRow } from "./types.js";
import type { DocDataGridColumns_ItemFragment } from "./useDocColumns.generated.js";

// Columns hidden by default. Owned here because every column it references is a
// generic (shared) column; the quote-only columns are never hidden.
const DOC_COLUMNS_INITIAL_STATE: GridInitialState = {
  columns: {
    columnVisibilityModel: {
      decisionBehavior: false,
      description: false,
      productTitle: false,
      productTitleVisibility: false,
      productDescription: false,
      productDescriptionVisibility: false,
      manufacturerArticleNumber: false,
      productSupplier: false,
      productBrandLine: false,
      productCategory: false,
      productGtin: false,
      productSapConfig: false,
      materialFactor: false,
      workFactor: false,
    },
  },
};

/**
 * The item shape the shared columns require. A consuming query satisfies it by
 * spreading the `DocDataGridColumns_Item` fragment (see useDocColumns.gql).
 */
export type DocColumnsItem = DocDataGridColumns_ItemFragment;

interface Options<T extends DocColumnsItem> {
  // `null` for templates (which have no project). The calc/info/product
  // mutations all accept a null projectId; the project-scoped SAP config column
  // is omitted when projectId is null.
  projectId: string | null;
  docId: string;
  canEdit: boolean;
  itemCanBeEdited?: (item: T) => boolean;
  expandedItemIds: string[] | undefined;
  viewerIsContractor: boolean;
}

export interface DocColumnGroups<T extends DocColumnsItem> {
  // The three decision/contingency columns are returned individually because
  // the quote layout interleaves its two quote-only columns (template
  // placeholder, wizard buttons) between `decisionOption` and `decisionBehavior`.
  contingencyCheckboxColumn: GridColDef<DocDataGridRow<T>>;
  decisionOptionColumn: GridColDef<DocDataGridRow<T>>;
  decisionBehaviorColumn: GridColDef<DocDataGridRow<T>>;
  descriptionColumn: GridColDef<DocDataGridRow<T>>;
  productColumns: GridColDef<DocDataGridRow<T>>[];
  calculationColumns: GridColDef<DocDataGridRow<T>>[];
  initialState: GridInitialState;
}

/**
 * Shared, document-type-agnostic column assembly for the doc data grid. Wires
 * the item-editing mutations and returns the columns as named groups; each
 * document variant (quote, template) composes them into its final ordered array
 * and adds its own document-specific columns.
 */
export function useDocColumns<T extends DocColumnsItem>(
  options: Options<T>
): DocColumnGroups<T> {
  const {
    projectId,
    docId,
    canEdit,
    itemCanBeEdited,
    expandedItemIds,
    viewerIsContractor,
  } = options;

  const { modifyItemCalculation } = useModifyItemCalculation({
    projectId,
    docId,
  });

  const { modifyItemInfo } = useModifyItemInfo({ projectId, docId });

  const { modifyItemProduct, createItemProduct } = useModifyItemProduct({
    projectId,
    docId,
  });

  const { setItemProduct } = useSetItemProduct({
    projectId,
    docId,
    expandedItemIds,
  });

  const { toggleContingency } = useContingencyCheckbox({ projectId, docId });

  const { setDecisionBehavior } = useDecisionBehaviorToggle({
    projectId,
    docId,
  });

  const { toggleDecisionOption } = useDecisionOptionToggle({
    projectId,
    docId,
    expandedItemIds,
  });

  const hasSapS4HanaIntegration = useHasSapS4HanaIntegration();
  const { showFisSolr, sapIntegrationSettings } = useFisSolr();

  const { t } = useTranslate(["QuoteItem", "Global", "Product"]);

  return React.useMemo((): DocColumnGroups<T> => {
    const contingencyCheckboxColumn = createContingencyCheckboxColumn<T>({
      headerName: t("Optional item", { ns: "QuoteItem" }),
      canEdit,
      toggleContingency,
    });

    const decisionOptionColumn = createDecisionOptionColumn<T>({
      headerName: t("Decision option", { ns: "QuoteItem" }),
      canEdit,
      toggleDecisionOption,
    });

    const decisionBehaviorColumn = createDecisionBehaviorColumn<T>({
      canEdit,
      setDecisionBehavior,
      headerName: t("Decision type", { ns: "QuoteItem" }),
    });

    const descriptionColumn = createDescriptionColumn<T>({
      canEdit,
      modifyItemInfo,
      itemCanBeEdited,
      headerName: t("Description", { ns: "QuoteItem" }),
    });

    const productColumns: GridColDef<DocDataGridRow<T>>[] = [
      createProductArticleNumberColumn<T>({
        headerName: t("Article no.", { ns: "Product" }),
        canEdit,
        showFisSolr,
        sapIntegrationSettings,
        onProductSelect: setItemProduct,
        onProductCreate: createItemProduct,
        docId,
      }),
      createProductTitleColumn<T>({
        headerName: t("Product title", { ns: "QuoteItem" }),
        canEdit,
        modifyItemProduct,
      }),
      ...(viewerIsContractor
        ? [
            createProductTitleVisibilityColumn<T>({
              headerName: t("Title visibility", { ns: "Product" }),
              canEdit,
              viewerIsContractor,
              modifyItemProduct,
            }),
          ]
        : []),
      createProductDescriptionColumn<T>({
        headerName: t("Product description", { ns: "QuoteItem" }),
        canEdit,
        modifyItemProduct,
      }),
      ...(viewerIsContractor
        ? [
            createProductDescriptionVisibilityColumn<T>({
              headerName: t("Description visibility", { ns: "Product" }),
              canEdit,
              viewerIsContractor,
              modifyItemProduct,
            }),
          ]
        : []),
      createManufacturerArticleNumberColumn<T>({
        headerName: t("Manufacturer article no.", { ns: "Product" }),
        canEdit,
        modifyItemProduct,
      }),
      createProductSupplierColumn<T>({
        headerName: t("Supplier", { ns: "Product" }),
      }),
      createProductBrandLineColumn<T>({
        headerName: t("Brand", { ns: "Product" }),
      }),
      createProductCategoryColumn<T>({
        headerName: t("Category", { ns: "Product" }),
      }),
      createProductGtinColumn<T>({
        headerName: t("GTIN", { ns: "Product" }),
      }),
      ...(hasSapS4HanaIntegration && projectId !== null
        ? [
            createProductSapConfigColumn<T>({
              headerName: "",
              projectId,
              docId,
            }),
          ]
        : []),
    ];

    const calculationColumns: GridColDef<DocDataGridRow<T>>[] = [
      createEstimatedQuantityColumn({
        projectId,
        docId,
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Qty", { ns: "QuoteItem" }),
      }),
      createQuantityUnitColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Unit", { ns: "QuoteItem" }),
      }),
      createPricePerUnitColumn({
        headerName: t("Price/Unit", { ns: "QuoteItem" }),
      }),
      createMaterialBuyingPriceColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Mat. price", { ns: "QuoteItem" }),
      }),
      createMaterialMarginColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Mat. margin", { ns: "QuoteItem" }),
      }),
      createMaterialFactorColumn({
        headerName: `${t("Mat.", { ns: "QuoteItem" })} ×`,
      }),
      createMaterialPricePerUnitColumn({
        headerName: t("Mat./Unit", { ns: "QuoteItem" }),
      }),
      createEstimatedTimeColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Work time", { ns: "QuoteItem" }),
      }),
      createWorkSellingRateColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Work rate", { ns: "QuoteItem" }),
      }),
      createWorkMarginColumn({
        canEdit,
        modifyItemCalculation,
        itemCanBeEdited,
        headerName: t("Work margin", { ns: "QuoteItem" }),
      }),
      createWorkFactorColumn({
        headerName: `${t("Work", { ns: "QuoteItem" })} ×`,
      }),
      createWorkPricePerUnitColumn({
        headerName: t("Work/Unit", { ns: "QuoteItem" }),
      }),
      createTimeTotalColumn({
        headerName: t("Time total", { ns: "QuoteItem" }),
      }),
      createPriceSubTotalColumn({
        headerName: t("Subtotal", { ns: "QuoteItem" }),
      }),
      createSavingIndicatorColumn<T>({
        headerName: t("Saving indicator", { ns: "Global" }),
      }),
    ];

    return {
      contingencyCheckboxColumn,
      decisionOptionColumn,
      decisionBehaviorColumn,
      descriptionColumn,
      productColumns,
      calculationColumns,
      initialState: DOC_COLUMNS_INITIAL_STATE,
    };
  }, [
    projectId,
    docId,
    canEdit,
    itemCanBeEdited,
    viewerIsContractor,
    t,
    modifyItemCalculation,
    modifyItemInfo,
    modifyItemProduct,
    setItemProduct,
    createItemProduct,
    toggleContingency,
    toggleDecisionOption,
    setDecisionBehavior,
    showFisSolr,
    sapIntegrationSettings,
    hasSapS4HanaIntegration,
  ]);
}
